import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { URLS } from '../../../utils/constants/urls';
import axiosInstance from '../../../utils/axios';
import {
    Alert,
    AlertColor,
    Avatar,
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import strings from '../../../common/Translation/Translate';
import { useParams } from 'react-router-dom';
import { routes } from '../../../utils/constants/routes';
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined';
import EventAvailableOutlined from '@mui/icons-material/EventAvailableOutlined';
import EventNoteOutlined from '@mui/icons-material/EventNoteOutlined';
import FlagOutlined from '@mui/icons-material/FlagOutlined';
import PersonOutline from '@mui/icons-material/PersonOutline';
import dayjs from 'dayjs';
import Notification from 'components/Notification';
import Navigation from '../../../utils/hooks/Navigation';
import { useCustomNavigate } from 'utils/hooks/useCustomNavigate';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import PageLoader from 'components/PageLoader';
import { setCloneFromRFQ } from 'utils/redux/reducer/booking-slice';
import { useDispatch } from 'react-redux';
import { createDropdownData } from '../../../utils/validators/HelperFunctions';
import BookingDiary from 'components/BookingDiary';
import ConfirmModal from 'components/ConfirmModal';
import { getRfqStatusColors } from './rfqStatusColors';
import { getAvatarPalette } from '../../../utils/avatarColors';
import { getInitials } from '../SalesManagement/SalesCalls/salesCallListDisplay';
import RfqFollowUps from './RfqFollowUps';

dayjs.extend(LocalizedFormat);
dayjs.extend(isSameOrAfter);

const LOST_REASON_MAX_LENGTH = 250;

const summaryPaperSx = {
  p: 2.25,
  borderRadius: 2,
  width: '100%',
  display: 'flex',
  flexDirection: 'column' as const,
  flex: 1,
};

function formatDisplayDate(value?: string | Date | null) {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('D MMM YYYY') : '—';
}

function formatPreferredDateRange(start?: string | Date | null, end?: string | Date | null) {
  const startDate = start ? dayjs(start) : null;
  const endDate = end ? dayjs(end) : null;
  if (!startDate?.isValid()) return '—';
  if (!endDate?.isValid() || endDate.isSame(startDate, 'day')) {
    return startDate.format('D MMM YYYY');
  }
  if (startDate.year() === endDate.year() && startDate.month() === endDate.month()) {
    return `${startDate.format('D')}-${endDate.format('D MMM YYYY')}`;
  }
  if (startDate.year() === endDate.year()) {
    return `${startDate.format('D MMM')} - ${endDate.format('D MMM YYYY')}`;
  }
  return `${startDate.format('D MMM YYYY')} - ${endDate.format('D MMM YYYY')}`;
}

function displayOrFallback(value: unknown, fallback: string) {
  const text = value == null ? '' : String(value).trim();
  if (!text || text.toUpperCase() === 'NA') return fallback;
  return text;
}

function InfoGridRow({
  cells,
  columns,
}: {
  cells: { label: string; value: ReactNode }[];
  columns?: number;
}) {
  const n = columns ?? cells.length;
  const xsSpan = n === 1 ? 12 : 6;
  const smSpan = n === 1 ? 12 : n === 2 ? 6 : 4;

  return (
    <Box sx={{ mb: 2, '&:last-of-type': { mb: 0 } }}>
      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
        {cells.map(c => (
          <Grid item xs={xsSpan} sm={smSpan} key={c.label}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, letterSpacing: 0.02, display: 'block', lineHeight: 1.2 }}>
              {c.label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word', mt: 0.25 }}>
              {c.value}
            </Typography>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
export const RFQDetails = () => {
  const { id } = useParams() as any;
  const dispatch = useDispatch();
  const navigate = useCustomNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  /** Sync guard so a second Save click in the same tick cannot start another PUT. */
  const savingRef = useRef(false);
  const [assigneeList, setAssigneeList] = useState<any>([]);
  const [followUpModes, setFollowUpModes] = useState<any>([]);
  const [rfqDetails, setRfqDetails] = useState<any>({});
  const [reason, setReason] = useState<string>('');
  const [showDiaryModal, setShowDiaryModal] = useState(false);
  const [showLostReasonModal, setShowLostReasonModal] = useState(false);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: any;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const closeLostReasonModal = () => {
    if (isLoading) return;
    setShowLostReasonModal(false);
    setReason('');
  };

  const handleCatch = useCallback((error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error.response.data?.error_description ?? error.response.data.message ?? error.message,
    });
  }, []);

  const getMasterData = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await axiosInstance.get(URLS.followUpDropdown);
      setAssigneeList(createDropdownData(data?.assignees, ['id', 'name']));
      setFollowUpModes(createDropdownData(data?.followUpModes, ['id', 'name']));
    } catch (error) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchRfqDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.get(URLS.viewRfq(id));
      const formatted = {
        ...data,
        followUps: data.tasks.map((d: any) => ({
          id: d.id,
          createdBy: d.createdBy,
          createdAt: d.createdAt,
          datetime: d.dueDate,
          mode: d.followUpMode ?? d.mode,
          assignedTo: d.assignedTo ? d.assignedTo.label : null,
          summary: d.description,
          attachmentUrl: d.attachmentUrl,
          fileName: d.fileName,
          fileSize: d.fileSize,
          status: d.status,
          completedAt: d.completedAt,
          completionReason: d.completionReason,
        })),
      };
      setRfqDetails(formatted);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  }, [id, handleCatch]);

  useEffect(() => {
    if (id) {
      (async () => {
        await fetchRfqDetails();
        await getMasterData();
      })();
    }
  }, [fetchRfqDetails, getMasterData, id]);

  const saveLostReason = useCallback(async () => {
    const trimmedReason = reason.trim();
    if (!trimmedReason || trimmedReason.length > LOST_REASON_MAX_LENGTH) return;
    if (savingRef.current || isLoading) return;
    savingRef.current = true;
    setIsLoading(true);

    try {
      const requestBody = { status: 'LOST', reason: trimmedReason };
      const { status } = await axiosInstance.put(URLS.dashboard_rfq_task(rfqDetails.id), requestBody);
      if (status === 200) {
        setShowLostReasonModal(false);
        setReason('');
        await fetchRfqDetails();
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      savingRef.current = false;
      setIsLoading(false);
    }
  }, [reason, rfqDetails, handleCatch, fetchRfqDetails, isLoading]);

  const getChipStyles = (status: string) => {
    const statusColors = getRfqStatusColors(status);
    if (statusColors) return statusColors;
    return { backgroundColor: '#FDECEC', color: '#C43232' };
  };

  const customerName = [rfqDetails.firstName, rfqDetails.lastName].filter(Boolean).join(' ').trim();
  const companyName = String(rfqDetails.companyName || '').trim();
  const avatarPalette = getAvatarPalette(customerName);
  const eventTypeValue = displayOrFallback(rfqDetails.eventSubtype, strings.rfq_not_specified);
  const pricePackageValue = String(rfqDetails.pricePackage || '').trim()
    ? rfqDetails.pricePackage
    : strings.rfq_not_selected;

  return (
    <Box sx={{ width: '100%' }}>
        {isLoading && <PageLoader />}
        {message.display && (
          <Notification
            isOpen={message.display}
            message={message.message}
            severity={message.severity as AlertColor}
            closeNotification={closeNotification}
          />
        )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: rfqDetails.status?.value === 'LOST' ? 1.5 : 2.5,
            }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, pr: 2 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                }}>
                {strings.rfq_title_list}
              </Typography>
              {rfqDetails.status?.value && (
                <Chip
                  label={rfqDetails.status.value.toLowerCase().replace('_', ' ')}
                  size="small"
                  sx={{
                    textTransform: 'capitalize',
                    fontWeight: 600,
                    ...getChipStyles(rfqDetails.status.value),
                  }}
                />
              )}
            </Stack>
            <Navigation url={-1} />
          </Box>
          {rfqDetails.status?.value === 'LOST' && (
            <Alert
              severity="error"
              icon={<FlagOutlined fontSize="inherit" />}
              sx={{
                mb: 3,
                py: 0.25,
                alignItems: 'center',
                '& .MuiAlert-message': { fontSize: '0.8125rem', fontWeight: 500, py: 0.25 },
              }}>
              {rfqDetails.updateReason
                ? (strings.formatString(strings.rfq_lost_banner_reason, rfqDetails.updateReason) as string)
                : strings.rfq_lost_banner}
            </Alert>
          )}

          <Grid container spacing={2} alignItems="stretch">
                <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                  <Paper variant="outlined" sx={summaryPaperSx}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <EventNoteOutlined sx={{ fontSize: 22, color: 'primary.main', flexShrink: 0 }} />
                      <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                        {strings.new_booking_section_event_details}
                      </Typography>
                    </Stack>
                    <Divider sx={{ mb: 1.5 }} />

                    <InfoGridRow
                      cells={[
                        { label: strings.rfq_column_createdDate, value: formatDisplayDate(rfqDetails.createdDate) },
                        {
                          label: strings.rfq_column_preferredDate,
                          value: formatPreferredDateRange(rfqDetails.preferredStartDate, rfqDetails.preferredEndDate),
                        },
                        { label: strings.rfq_column_timeslot, value: rfqDetails.timeSlot || '—' },
                      ]}
                    />
                    <InfoGridRow
                      cells={[
                        { label: strings.rfq_column_venue, value: rfqDetails.venue || '—' },
                        { label: strings.rfq_column_segment, value: rfqDetails.eventType || '—' },
                        { label: strings.new_booking_event_sub_type, value: eventTypeValue },
                      ]}
                    />
                    <InfoGridRow
                      columns={3}
                      cells={[
                        { label: strings.venueDetails_text_guests, value: rfqDetails.guests ?? '—' },
                        { label: strings.rfq_column_price_package, value: pricePackageValue },
                      ]}
                    />

                    {rfqDetails.placeOfEvent && (
                      <InfoGridRow
                        cells={[
                          { label: strings.customerDetailsPlaceOfEvent, value: rfqDetails.placeOfEvent },
                          { label: strings.customerDetailsAddressOfEvent, value: rfqDetails.addressOfEvent || '—' },
                        ]}
                      />
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                  <Paper variant="outlined" sx={summaryPaperSx}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <PersonOutline sx={{ fontSize: 22, color: 'primary.main', flexShrink: 0 }} />
                      <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                        {strings.tabs_section_customerDetails}
                      </Typography>
                    </Stack>
                    <Divider sx={{ mb: 1.5 }} />

                    <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2.5 }}>
                      <Avatar
                        sx={{
                          width: 44,
                          height: 44,
                          bgcolor: avatarPalette.bg,
                          color: avatarPalette.color,
                          fontSize: '0.9rem',
                          fontWeight: 600,
                        }}>
                        {getInitials(customerName)}
                      </Avatar>
                      <Box sx={{ minWidth: 0, pt: 0.25 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25, fontSize: '1.15rem' }}>
                          {customerName || '—'}
                        </Typography>
                        {companyName ? (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
                            {companyName}
                          </Typography>
                        ) : null}
                      </Box>
                    </Stack>

                    <InfoGridRow
                      cells={[
                        { label: strings.rfq_column_email, value: rfqDetails.email || '—' },
                        { label: strings.rfq_column_mobile, value: rfqDetails.mobile || '—' },
                      ]}
                    />
                  </Paper>
                </Grid>
              </Grid>

          <Stack
            direction="row"
            flexWrap="wrap"
            justifyContent="flex-end"
            sx={{ mt: 2, gap: 1.5 }}>
              {!rfqDetails.isConverted && rfqDetails.status?.value !== 'LOST' && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<FlagOutlined />}
                  onClick={() => setShowLostReasonModal(true)}
                  size="medium"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 1.5,
                    px: 2,
                    boxShadow: 'none',
                    '&:hover': { boxShadow: 'none' },
                  }}>
                  {strings.rfq_mark_as_lost}
                </Button>
              )}

              {dayjs(rfqDetails.preferredStartDate).isSameOrAfter(dayjs()) && !rfqDetails.isConverted && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<CalendarMonthOutlined />}
                    disabled={rfqDetails.status?.value === 'LOST'}
                    onClick={() => setShowDiaryModal(true)}
                    size="medium"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 1.5,
                      px: 2,
                      boxShadow: 'none',
                      '&:hover': { boxShadow: 'none' },
                    }}>
                    {strings.rfq_view_calendar}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<EventAvailableOutlined />}
                    disabled={rfqDetails.status?.value === 'LOST'}
                    onClick={() => {
                      dispatch(setCloneFromRFQ(true));
                      navigate(`${routes.addBooking}/${id}`);
                    }}
                    size="medium"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 1.5,
                      px: 2,
                      boxShadow: 'none',
                      '&:hover': { boxShadow: 'none' },
                    }}>
                    {strings.convertIntoBooking}
                  </Button>
                </>
              )}
              {rfqDetails.isConverted && rfqDetails.bookingLineItemId && (
                <Button
                  variant="contained"
                  startIcon={<EventAvailableOutlined />}
                  onClick={() => navigate(routes.bookings + '/' + rfqDetails.bookingLineItemId)}
                  size="medium"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 1.5,
                    px: 2,
                    boxShadow: 'none',
                    '&:hover': { boxShadow: 'none' },
                  }}>
                  {strings.viewBookingDetails}
                </Button>
              )}
          </Stack>

        {rfqDetails.id ? (
          <Box sx={{ mt: 3 }}>
            <RfqFollowUps
              rfqId={rfqDetails.id}
              followUps={rfqDetails.followUps || []}
              assigneeList={assigneeList}
              followUpModes={followUpModes}
              isLost={rfqDetails.status?.value === 'LOST'}
              onRefresh={fetchRfqDetails}
            />
          </Box>
        ) : null}
      <ConfirmModal
        open={showLostReasonModal}
        closeModal={closeLostReasonModal}
        handleYes={saveLostReason}
        tone="danger"
        icon={<FlagOutlined sx={{ fontSize: 28 }} />}
        maxWidth="sm"
        title={strings.rfq_mark_as_lost}
        subtitle={strings.rfq_lost_dialog_subtitle}
        message={`${strings.delete_cannot_undo_text}\n\n${strings.rfq_lost_dialog_prompt}`}
        entityName={[customerName, rfqDetails.venue].filter(Boolean).join(' · ') || undefined}
        confirmLabel={strings.rfq_mark_as_lost}
        loading={isLoading}
        confirmDisabled={!reason.trim() || reason.trim().length > LOST_REASON_MAX_LENGTH}
        confirmAutoFocus={false}>
        <TextField
          autoFocus
          required
          fullWidth
          multiline
          minRows={3}
          maxRows={6}
          label={strings.rfq_lost_reason_label}
          placeholder={strings.rfq_lost_reason_placeholder}
          value={reason}
          onChange={e => setReason(e.target.value)}
          error={reason.trim().length > LOST_REASON_MAX_LENGTH}
          helperText={
            reason.trim().length > LOST_REASON_MAX_LENGTH
              ? (strings.formatString(strings.exceeded_character_limit, LOST_REASON_MAX_LENGTH) as string)
              : `${reason.trim().length}/${LOST_REASON_MAX_LENGTH}`
          }
          FormHelperTextProps={{ sx: { mx: 0, textAlign: 'right' } }}
        />
      </ConfirmModal>
      {showDiaryModal && <BookingDiary open={showDiaryModal} onClose={() => setShowDiaryModal(false)} />}
    </Box>
  );
};
